import { useAppStore } from '@/store/appStore'
import { useEffect } from 'react'

/** While the app is open and in front, send at most this often even if nothing happens. */
const POLL_MS = 30_000

/**
 * When to send without being asked: on start, when the connection comes back,
 * when the app returns to the foreground, and on a slow poll while visible so
 * marks made on another phone show up here too.
 */
export function useSyncTriggers(enabled: boolean): void {
  const syncNow = useAppStore((s) => s.syncNow)
  const setOnline = useAppStore((s) => s.setOnline)

  useEffect(() => {
    if (!enabled) return
    const visible = () => document.visibilityState === 'visible'
    const onOnline = () => setOnline(true)
    const onOffline = () => setOnline(false)
    const onVisible = () => {
      if (visible()) void syncNow()
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onVisible)
    const poll = setInterval(() => {
      if (visible() && navigator.onLine !== false) void syncNow()
    }, POLL_MS)
    void syncNow()
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onVisible)
      clearInterval(poll)
    }
  }, [enabled, syncNow, setOnline])
}
